import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  Check,
  ChevronDown,
  X,
} from "lucide-react";

import serumImg from "../assests/faceimg.webp"; // Path verify kar lein

const productsData = [
  {
    id: "glow-serum",
    name: "Glow Niacinamide Serum",
    category: "Serums & Oils",
    concern: "Brightening",
    price: 48.0,
    rating: 4.9,
    reviews: 124,
    image: serumImg,
    tag: "Bestseller",
    isNew: false,
  },
  {
    id: "barrier-cream",
    name: "Ceramide Hydrating Barrier Cream",
    category: "Moisturizers",
    concern: "Barrier Care",
    price: 52.0,
    rating: 4.8,
    reviews: 98,
    image: serumImg,
    tag: "New",
    isNew: true,
  },
  {
    id: "rice-toner",
    name: "Radiant Rice Essence Toner",
    category: "Cleansers & Toners",
    concern: "Hydration",
    price: 36.0,
    rating: 4.7,
    reviews: 86,
    image: serumImg,
    tag: "Trending",
    isNew: false,
  },
  {
    id: "peptide-night-oil",
    name: "Night Recovery Peptide Oil",
    category: "Serums & Oils",
    concern: "Anti-Aging",
    price: 64.0,
    rating: 5.0,
    reviews: 42,
    image: serumImg,
    tag: "Luxury",
    isNew: true,
  },
  {
    id: "cica-soothing-gel",
    name: "Cica Calm Soothing Gel",
    category: "Moisturizers",
    concern: "Calming",
    price: 42.0,
    rating: 4.6,
    reviews: 65,
    image: serumImg,
    tag: "Gentle Care",
    isNew: false,
  },
  {
    id: "ginseng-sheet-mask",
    name: "Ginseng Youth Sheet Mask (Set of 5)",
    category: "Sheet Masks",
    concern: "Anti-Aging",
    price: 28.0,
    rating: 4.9,
    reviews: 150,
    image: serumImg,
    tag: "Set",
    isNew: false,
  },
];

const categories = ["All", "Serums & Oils", "Moisturizers", "Cleansers & Toners", "Sheet Masks"];
const concerns = ["All Concerns", "Hydration", "Brightening", "Barrier Care", "Anti-Aging", "Calming"];

const Shop = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedConcern, setSelectedConcern] = useState("All Concerns");
  const [sortBy, setSortBy] = useState("featured");
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [wishlistIds, setWishlistIds] = useState([]);
  const [addedCartId, setAddedCartId] = useState(null);

  // Toggle Wishlist
  const toggleWishlist = (id) => {
    if (wishlistIds.includes(id)) {
      setWishlistIds(wishlistIds.filter((item) => item !== id));
    } else {
      setWishlistIds([...wishlistIds, id]); 
    }
  };

  // Add to Cart Simulator
  const handleAddToCart = (id) => {
    setAddedCartId(id);
    setTimeout(() => setAddedCartId(null), 2000);
  };

  // Filter Logic
  const filteredProducts = productsData.filter((product) => {
    const categoryMatch = selectedCategory === "All" || product.category === selectedCategory;
    const concernMatch = selectedConcern === "All Concerns" || product.concern === selectedConcern;
    return categoryMatch && concernMatch;
  });

  // Sort Logic
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === "price-low") return a.price - b.price;
    if (sortBy === "price-high") return b.price - a.price;
    if (sortBy === "rating") return b.rating - a.rating;
    return 0; // Default Featured
  });

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#FAF8F5] py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header Banner */}
        <div className="text-center space-y-3 py-6 border-b border-[#E8E2D9]">
          <span className="text-[11px] font-semibold uppercase tracking-[3px] text-[#9D7D4F] inline-flex items-center gap-1.5">
            <Sparkles size={13} /> Seoul Formulations
          </span>
          <h1 className="Cormorant Garamond text-3xl sm:text-4xl font-semibold text-[#1F1B18]">
            Explore All Skincare Essentials
          </h1>
          <p className="text-xs text-gray-500 max-w-lg mx-auto leading-relaxed">
            Dermatologist-formulated rituals powered by potent Korean botanicals and active ingredients.
          </p>
        </div>

        {/* Toolbar & Filter Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-[#E8E2D9] shadow-2xs">
          
          {/* Active Product Count & Mobile Filter Trigger */}
          <div className="flex items-center justify-between w-full sm:w-auto gap-4">
            <button
              onClick={() => setIsFilterDrawerOpen(true)}
              className="lg:hidden flex items-center gap-2 border border-[#E8E2D9] bg-[#FAF8F5] px-4 py-2 rounded-xl text-xs font-semibold text-[#1F1B18]"
            >
              <SlidersHorizontal size={14} /> Filter
            </button>
            <p className="text-xs font-medium text-gray-500">
              Showing <span className="text-black font-semibold">{sortedProducts.length}</span> Products
            </p>
          </div>

          {/* Desktop Sort Dropdown */}
          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <label className="text-xs font-medium text-gray-400 uppercase tracking-wider hidden sm:block">
              Sort By:
            </label>
            <div className="relative w-full sm:w-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-auto appearance-none bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2 pl-4 pr-10 text-xs font-semibold text-[#1F1B18] focus:outline-none focus:border-[#9D7D4F] cursor-pointer"
              >
                <option value="featured">Featured & Bestsellers</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Main Section: Sidebar Filters + Products Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Desktop Sidebar Filters */}
          <aside className="hidden lg:block lg:col-span-3 bg-white p-6 rounded-3xl border border-[#E8E2D9] shadow-2xs space-y-6 sticky top-24">
            
            {/* Category Filter */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[2px] text-[#9D7D4F] mb-3">
                Category
              </h3>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition flex items-center justify-between ${
                      selectedCategory === cat
                        ? "bg-[#FAF8F5] text-[#9D7D4F] font-semibold border border-[#E8E2D9]"
                        : "text-gray-600 hover:text-black hover:bg-[#FAF8F5]"
                    }`}
                  >
                    <span>{cat}</span>
                    {selectedCategory === cat && <Check size={12} />}
                  </button>
                ))}
              </div>
            </div>

            <hr className="border-[#E8E2D9]/60" />

            {/* Concern Filter */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[2px] text-[#9D7D4F] mb-3">
                Skin Concern
              </h3>
              <div className="space-y-1">
                {concerns.map((c) => (
                  <button
                    key={c}
                    onClick={() => setSelectedConcern(c)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition flex items-center justify-between ${
                      selectedConcern === c
                        ? "bg-[#FAF8F5] text-[#9D7D4F] font-semibold border border-[#E8E2D9]"
                        : "text-gray-600 hover:text-black hover:bg-[#FAF8F5]"
                    }`}
                  >
                    <span>{c}</span>
                    {selectedConcern === c && <Check size={12} />}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filter Action */}
            {(selectedCategory !== "All" || selectedConcern !== "All Concerns") && (
              <button
                onClick={() => {
                  setSelectedCategory("All");
                  setSelectedConcern("All Concerns");
                }}
                className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded-xl font-semibold transition"
              >
                Reset Filters
              </button>
            )}

          </aside>

          {/* Products Grid */}
          <main className="lg:col-span-9">
            {sortedProducts.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-[#E8E2D9] my-4">
                <p className="font-serif text-lg text-[#1F1B18]">No products match selected filters.</p>
                <button
                  onClick={() => {
                    setSelectedCategory("All");
                    setSelectedConcern("All Concerns");
                  }}
                  className="mt-3 text-xs text-[#9D7D4F] font-semibold underline"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden shadow-2xs hover:shadow-md transition-all duration-300 flex flex-col justify-between group"
                  >
                    {/* Image Area */}
                    <div className="relative aspect-square bg-[#F7F4EF] overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                      />

                      {/* Tag */}
                      {product.tag && (
                        <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-md text-[#1F1B18] text-[9px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#E8E2D9]">
                          {product.tag}
                        </span>
                      )}

                      {/* Wishlist Heart */}
                      <button
                        onClick={() => toggleWishlist(product.id)}
                        className={`absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-md rounded-full transition shadow-sm ${
                          wishlistIds.includes(product.id) ? "text-red-500" : "text-gray-400 hover:text-black"
                        }`}
                      >
                        <Heart size={15} fill={wishlistIds.includes(product.id) ? "currentColor" : "none"} />
                      </button>
                    </div>

                    {/* Content Area */}
                    <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                      <div>
                        <span className="text-[10px] uppercase tracking-widest text-[#9D7D4F] font-semibold block mb-1">
                          {product.category}
                        </span>
                        <Link
                          to={`/product/${product.id}`}
                          className="font-serif font-medium text-base text-[#1F1B18] hover:text-[#9D7D4F] transition line-clamp-1"
                        >
                          {product.name}
                        </Link>
                        
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-sm font-semibold text-[#1F1B18]">
                            ${product.price.toFixed(2)}
                          </p>
                          <span className="text-[11px] text-gray-400">
                            ★ {product.rating} ({product.reviews})
                          </span>
                        </div>
                      </div>

                      {/* Add to Cart Button */}
                      <button
                        onClick={() => handleAddToCart(product.id)}
                        className={`w-full py-2.5 rounded-xl text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 transition duration-300 ${
                          addedCartId === product.id
                            ? "bg-emerald-600 text-white"
                            : "bg-[#1F1B18] hover:bg-[#9D7D4F] text-white shadow-xs"
                        }`}
                      >
                        {addedCartId === product.id ? (
                          <>
                            <Check size={14} /> Added to Bag
                          </>
                        ) : (
                          <>
                            <ShoppingBag size={14} /> Add to Cart
                          </>
                        )}
                      </button>
                    </div>

                  </div>
                ))}
              </div>
            )}
          </main>

        </div>

      </div>

      {/* Mobile Drawer Filter */}
      {isFilterDrawerOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          <div onClick={() => setIsFilterDrawerOpen(false)} className="fixed inset-0 bg-black/50 backdrop-blur-xs" />
          <div className="fixed inset-y-0 left-0 max-w-xs w-full bg-white p-6 shadow-2xl z-10 flex flex-col justify-between overflow-y-auto">
            <div>
              <div className="flex items-center justify-between border-b border-[#E8E2D9] pb-4 mb-6">
                <h3 className="font-serif text-lg font-semibold">Filter Products</h3>
                <button onClick={() => setIsFilterDrawerOpen(false)}><X size={20} /></button>
              </div>

              {/* Mobile Filter Options */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-[#9D7D4F] mb-3">Categories</h4>
                  <div className="space-y-1">
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => { setSelectedCategory(cat); setIsFilterDrawerOpen(false); }}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs ${selectedCategory === cat ? "bg-[#FAF8F5] font-bold text-[#9D7D4F]" : "text-gray-600"}`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setIsFilterDrawerOpen(false)}
              className="w-full bg-[#1F1B18] text-white py-3 rounded-xl text-xs font-semibold uppercase tracking-widest mt-6"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;